// A low-poly freight truck — a flatbed chassis with six wheels, a stake-sided
// cargo bed at the back and a cab up front. The cab faces +z (the model's
// forward). Ported 1:1 from age-of-mcp's classic truck.ts, restoring its `axle`
// helper and bed/cab composition. The classic source scales a freight block by a
// cargo fill step; this gallery model shows the empty bed (fill step 0).
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const CAB    = "#b8462f";   // red cab
const BED    = "#7a5230";   // timber cargo bed
const STAKE  = "#5e3f24";   // bed side stakes
const CHASS  = "#3a3530";   // dark chassis / bumpers
const WHEEL  = "#26211b";   // tyres
const HUB    = "#8a8580";   // hubs
const GLASS  = "#9fc7d8";   // windscreen
const CARGO  = "#c8a45c";   // lashed freight in the bed

const HW   = 0.17;   // half-width (x)
const WR   = 0.075;  // wheel radius

// The cargo load is quantised into this many height steps.
const TRUCK_FILL_STEPS = 4;

// A wheel pair on an axle at z = `z`.
function axle(z: number): Part[] {
  const out: Part[] = [];
  for (const sx of [-1, 1]) {
    out.push({
      geometry: new THREE.CylinderGeometry(WR, WR, 0.05, 10),
      color: WHEEL,
      position: [sx * (HW + 0.01), WR, z],
      rotation: [0, 0, Math.PI / 2],
    });
    out.push({
      geometry: new THREE.CylinderGeometry(0.03, 0.03, 0.06, 6),
      color: HUB,
      position: [sx * (HW + 0.02), WR, z],
      rotation: [0, 0, Math.PI / 2],
    });
  }
  return out;
}

// `v` is the cargo FILL step (0..TRUCK_FILL_STEPS): the bed shows a freight
// block whose height grows with how loaded the truck is.
function partsFor(v: number): Part[] {
  const parts: Part[] = [];
  const chassisY = WR + 0.02;

  // Chassis rail.
  parts.push({ geometry: new THREE.BoxGeometry(2 * HW + 0.02, 0.05, 0.62), color: CHASS, position: [0, chassisY, -0.02] });

  // Wheels: front axle + rear bogie (two close axles).
  parts.push(...axle(0.2));
  parts.push(...axle(-0.16));
  parts.push(...axle(-0.26));

  // Cargo bed (timber floor + stake sides), at the back (-z).
  const bedY = chassisY + 0.05;
  parts.push({ geometry: new THREE.BoxGeometry(2 * HW, 0.04, 0.42), color: BED, position: [0, bedY, -0.12] });
  parts.push({ geometry: new THREE.BoxGeometry(0.03, 0.13, 0.42), color: STAKE, position: [ HW - 0.015, bedY + 0.07, -0.12] });
  parts.push({ geometry: new THREE.BoxGeometry(0.03, 0.13, 0.42), color: STAKE, position: [-HW + 0.015, bedY + 0.07, -0.12] });
  parts.push({ geometry: new THREE.BoxGeometry(2 * HW, 0.13, 0.03), color: STAKE, position: [0, bedY + 0.07, -0.32] });

  // Freight in the bed, scaled to the load (v / TRUCK_FILL_STEPS).
  const fill = Math.max(0, Math.min(1, v / TRUCK_FILL_STEPS));
  if (fill > 0) {
    const maxH = 0.12;                 // just below the stake tops (0.13)
    const h = Math.max(0.02, fill * maxH);
    parts.push({
      geometry: new THREE.BoxGeometry(2 * HW - 0.04, h, 0.38),
      color: CARGO,
      position: [0, bedY + 0.02 + h / 2, -0.12],
    });
  }

  // Cab up front (+z) with a windscreen.
  const cabY = chassisY + 0.04;
  parts.push({ geometry: new THREE.BoxGeometry(2 * HW, 0.22, 0.22), color: CAB, position: [0, cabY + 0.11, 0.20] });
  // Sloped roof front.
  parts.push({ geometry: new THREE.BoxGeometry(2 * HW - 0.02, 0.12, 0.10), color: GLASS, position: [0, cabY + 0.16, 0.31], rotation: [0.5, 0, 0] });
  // Front bumper.
  parts.push({ geometry: new THREE.BoxGeometry(2 * HW + 0.04, 0.05, 0.04), color: CHASS, position: [0, chassisY + 0.02, 0.33] });

  return parts;
}

export const truck = defineModel({
  id: "truck",
  batchId: "standard",
  name: "Truck",
  tags: ["vehicle","truck","low-poly","age-of-mcp","classic"],
  summary: "Low-poly truck.",
  description: "Ported from the age-of-mcp gallery.",
  author: "age-of-mcp",
  footprint: 0.5,
  parts: () => partsFor(0),
  animations: [],
});
